import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import authEn from "./locales/en/auth.json";
import billingEn from "./locales/en/billing.json";
import botsEn from "./locales/en/bots.json";
import commonEn from "./locales/en/common.json";
import errorsEn from "./locales/en/errors.json";
import modulesEn from "./locales/en/modules.json";
import authEs from "./locales/es/auth.json";
import billingEs from "./locales/es/billing.json";
import botsEs from "./locales/es/bots.json";
import commonEs from "./locales/es/common.json";
import errorsEs from "./locales/es/errors.json";
import modulesEs from "./locales/es/modules.json";
import authPt from "./locales/pt/auth.json";
import billingPt from "./locales/pt/billing.json";
import botsPt from "./locales/pt/bots.json";
import commonPt from "./locales/pt/common.json";
import errorsPt from "./locales/pt/errors.json";
import modulesPt from "./locales/pt/modules.json";

export const LOCALE_STORAGE_KEY = "botwsp.locale";
export const FALLBACK_LOCALE = "es";
export const SUPPORTED_LOCALES = ["es", "en", "pt"] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const LOCALE_LABELS: Record<Locale, string> = {
  es: "Español",
  en: "English",
  pt: "Português",
};

const resources = {
  en: {
    common: commonEn,
    auth: authEn,
    bots: botsEn,
    billing: billingEn,
    modules: modulesEn,
    errors: errorsEn,
  },
  es: {
    common: commonEs,
    auth: authEs,
    bots: botsEs,
    billing: billingEs,
    modules: modulesEs,
    errors: errorsEs,
  },
  pt: {
    common: commonPt,
    auth: authPt,
    bots: botsPt,
    billing: billingPt,
    modules: modulesPt,
    errors: errorsPt,
  },
};

function detectLocale(): Locale {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored && (SUPPORTED_LOCALES as readonly string[]).includes(stored)) {
      return stored as Locale;
    }
    const candidates =
      typeof navigator !== "undefined"
        ? (navigator.languages ?? [navigator.language])
        : [];
    for (const candidate of candidates) {
      const base = candidate.split("-")[0]?.toLowerCase();
      if (base && (SUPPORTED_LOCALES as readonly string[]).includes(base)) {
        return base as Locale;
      }
    }
  } catch {
    // localStorage or navigator unavailable (SSR/tests): fall through.
  }
  return FALLBACK_LOCALE;
}

void i18n.use(initReactI18next).init({
  resources,
  lng: detectLocale(),
  fallbackLng: FALLBACK_LOCALE,
  interpolation: { escapeValue: false },
});

export { i18n };

export function isLocale(value: string | null): value is Locale {
  return value !== null && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

export function currentLocale(): Locale {
  const resolved = i18n.resolvedLanguage ?? i18n.language;
  return isLocale(resolved) ? resolved : FALLBACK_LOCALE;
}

export async function changeLocale(locale: Locale): Promise<void> {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Persistence is best-effort; the UI language still changes.
  }
  await i18n.changeLanguage(locale);
}
